import addMonths from 'date-fns/fp/addMonths/index.js';
import format from 'date-fns/fp/format/index.js';
import parse from 'date-fns/fp/parse/index.js';
import { CurrencyConstantS } from '../../../Currency/S/Constant/module/CurrencyConstantS.js';
import { _getBusinessDayAfter } from '../../../Holiday/S/fs.js';
import { MShopUtilConstantS } from '../../../MShop/Util/S/Constant/module/MShopUtilConstantS.js';
import { BANK_TYPES } from '../../BankAccount/S/constant.js';
import {
  INCOME_TAX_RATE,
  PG_COMMISSION_RATE,
  RESIDENT_TAX_RATE,
  VAT_RATE,
} from '../../SettingAdjustments/S/constant.js';
import { MembershipAdjustment } from '../../../../services/MarppleShop/features/Membership/type';
import * as fxTs from '@fxts/core';
import {
  addBigDecimal,
  decimalFloorTo,
  divBigDecimal,
  mulBigDecimal,
  StringNumber,
  subBigDecimal,
} from '../../../Util/Number/S/Function/bigDecimal';
import Decimal from 'decimal.js';
import { UtilS } from '../../../Util/S/Function/module/UtilS';
import { divDecimal, mulDecimal } from '../../../Util/Number/S/Function/decimal';
import { isAdjFeeDate } from '../../Adjustment/S/util';
import { makeAdjWithoutCommissionAmount } from '../../SettingAdjustments/S/util';
import dateFnsTz from 'date-fns-tz';

interface Adjustment {
  profit: number;
  bonus_profit?: number;
  crew_margin?: number;
  no_commission?: boolean;
  exchange_rate?: number;
  crew_id?: number;
  fee_amount?: number;
  paid_at: Date;
  created_at: Date;
}

const getVat = (amount: StringNumber) =>
  Decimal.round(subBigDecimal(amount, divBigDecimal(amount, addBigDecimal(1, VAT_RATE)))).toString(); // amount - (amount / (1 + vat_rate))

const getPreTax = (amount: StringNumber) => {
  /*
   * 1 의 자리 내림
   * */
  const round10 = decimalFloorTo(1);
  const income_tax = round10(mulBigDecimal(amount, INCOME_TAX_RATE)); // 소득세
  const resident_tax = round10(mulBigDecimal(income_tax, RESIDENT_TAX_RATE)); // 주민세
  return { income_tax, resident_tax };
};

const getPersonTaxDetail = (amount: StringNumber) => {
  // 부가세
  const vat = getVat(amount);
  // 원천징수액
  const { income_tax, resident_tax } = getPreTax(subBigDecimal(amount, vat));
  return { vat, income_tax, resident_tax };
};

export const getWithdrawLimitDate = (withdraw_date: Date, holidays: Date[]) =>
  fxTs.pipe(
    new Date(withdraw_date),
    (date) => {
      if (date < new Date('2025-11-01 00:00:00+09:00')) {
        if (date.getDate() > 10) {
          return addMonths(1)(date);
        }
      }
      return date;
    },
    (date) => zonedTimeToUtc(date, 'Asia/Seoul'),
    format('yyyyMM10 000000'),
    parse(new Date())('yyyyMMdd HHmmss'),
    (date) => {
      const days = date.getFullYear() === 2025 && date.getMonth() < 10 ? 9 : 5;
      return _getBusinessDayAfter(days, date, holidays);
    },
  );

const { zonedTimeToUtc } = dateFnsTz;

export const getExpectedWithdrawDate = (withdraw_date: Date, holidays: Date[], is_wait: boolean) =>
  fxTs.pipe(
    new Date(withdraw_date),
    (date) => {
      if (is_wait) {
        if (date.getDate() > 2) {
          return addMonths(1)(date);
        }
      }
      /* 이 아래 조건들은 진행중만 */
      if (date < new Date('2025-11-01 00:00:00+09:00')) {
        if (date.getDate() > 10) {
          return addMonths(1)(date);
        }
      }
      return date;
    },
    (date) => zonedTimeToUtc(date, 'Asia/Seoul'),
    format('yyyyMM10 000000'),
    parse(new Date())('yyyyMMdd HHmmss'),
    (date) => {
      const days = date.getFullYear() === 2025 && date.getMonth() < 10 ? 9 : 5;
      return _getBusinessDayAfter(days, date, holidays);
    },
  );

export const calcTotalWithdraw = (withdraws: Array<{ approved_at: Date | null; real_amount: number }>) =>
  fxTs.pipe(
    withdraws,
    fxTs.filter((w) => w.approved_at),
    fxTs.pluck('real_amount'),
    (iterable) => fxTs.reduce((a, b) => fxTs.add(a, b), 0, iterable),
  );

export const getWithdrawTaxAmount = (amount: string | number, bank_account_type: string) => {
  const is_company = bank_account_type == BANK_TYPES.COMPANY;
  const is_foreign = bank_account_type == BANK_TYPES.FOREIGN;

  const { vat, income_tax, resident_tax } = is_company
    ? { vat: '0', income_tax: '0', resident_tax: '0' }
    : is_foreign
      ? { vat: getVat(amount), income_tax: '0', resident_tax: '0' }
      : getPersonTaxDetail(amount);

  return { vat, income_tax, resident_tax, amount: subBigDecimal(amount, vat, income_tax, resident_tax) };
};

export const sumProfit = ({
  profit,
  bonus_profit,
  crew_margin,
}: {
  profit?: number;
  bonus_profit?: number;
  crew_margin?: number;
}): number => {
  return (profit || 0) + (bonus_profit || 0) + (crew_margin || 0);
};

export const getWithoutFeeAndPg = (currency?: string) => (adj: Adjustment) => {
  return fxTs.pipe(
    sumProfit(adj),
    (profit) => {
      return profit - (adj.fee_amount || 0) - getPgCommissionAmount(currency)(adj);
    },
    (settlement: number) => {
      if (!currency || currency == CurrencyConstantS.CURRENCY.KRW) {
        return Math.round(settlement);
      }
      return UtilS.floorTo(-2, divDecimal(settlement, adj.exchange_rate));
    },
  );
};

// currency 조건 고려 안함 (crew 의 외부 통화)
export const getCalcMembershipWithdrawableAmount = (adjustments: MembershipAdjustment[]) => {
  return fxTs.pipe(
    adjustments,
    fxTs.map((adj) => {
      const { without_commission_amount } = makeAdjWithoutCommissionAmount(adj);
      return without_commission_amount;
    }),
    (iterable) => fxTs.reduce((a, b) => addBigDecimal(a, b), '0', iterable),
  );
};

export const getPgCommissionAmount =
  (currency?: string) =>
  (adj: Adjustment): number => {
    return fxTs.pipe(
      sumProfit(adj),
      (profit) => {
        if (isAdjFeeDate(adj.paid_at)) {
          if (
            /**
             * @desc
             * 웹툰 프렌즈에 한해 잘못된 로직으로 일단 보이게
             */
            adj.created_at &&
            adj.crew_id === 232 &&
            new Date(adj.created_at) > new Date('2025-04-01T00:00:00+09:00') &&
            new Date(adj.created_at) < new Date('2025-05-13T18:00:00+09:00')
          ) {
            return adj.no_commission ? 0 : mulDecimal(adj.profit, PG_COMMISSION_RATE);
          }

          /**
           * @desc
           * 이전 날짜는 잘못된 로직으로 돌아가고 있었는데 데이터가 해당 로직으로 남아있기 때문에
           * 로직을 수정하진 않고 그 외의 날짜는 아래의 정상 로직으로 돌아가게 했습니다.
           */
          return adj.no_commission ? 0 : mulDecimal(profit, PG_COMMISSION_RATE);
        } else {
          /* profit bonus_profit crew_margin 합친 수익 */
          return adj.no_commission ? 0 : mulDecimal(profit - (adj.fee_amount ?? 0), PG_COMMISSION_RATE);
        }
      },
      (settlement) => {
        if (!currency || currency == CurrencyConstantS.CURRENCY.KRW) {
          return Math.round(settlement);
        }
        return UtilS.floorTo(-2, divDecimal(settlement, adj.exchange_rate));
      },
    );
  };

export const changeStringColumnToNumber = fxTs.evolve({
  amount: Number,
  dtg_amount: Number,
  pb_amount: Number,
  pod_amount: Number,
  real_amount: Number,
  tpl_amount: Number,
});

// 추후 수정할 일 있을 때, 멤버십 정산도 함께 수정되어야 하면 찾아주세요 @ryan, 멤버십 정산에서는 아래 함수를 쓰지 않았습니다.
export const setAbstractNoPgCommissionByAdjustment = (adjs: Adjustment[]) => {
  return adjs.map((adj) => {
    /* TODO 일단 SOOPTORE만 no_commission 인데 앞으로 어떻게할지 기획이 나오면 그때 생각해보자... */
    const no_commission = adj.crew_id == MShopUtilConstantS.SOOP_MARPPLESHOP_CREW_ID;
    return { ...adj, no_commission };
  });
};

export const makePartnerId = (store_id: string) => `${store_id}_${format('yyyyMMdd')(new Date())}`;
